import React from 'react';
import { Box, Grid, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { formatCurrency } from '../DrillDown/formatters';

interface Fornecedor {
  nome: string;
  cnpj_cpf: string;
  total_credito_icms: number;
  documentos: number;
  participacao_percentual?: number;
}

interface Cfop {
  cfop: string;
  total_credito_icms: number;
  documentos: number;
}

interface Estatisticas {
  total_credito_icms: number;
  ticket_medio: number;
  total_documentos: number;
  max_nota: number;
  min_nota: number;
}

interface DataResponse {
  periodo: string;
  fonte_dados?: string;
  sped_fiscal_id?: number | string | null;
  sped_fiscal_ids?: Array<number | string>;
  estatisticas: Estatisticas;
  top_fornecedores: Fornecedor[];
  top_cfops: Cfop[];
}

const IcmsCreditosPeriodoDrillDownContent: React.FC<{ data: DataResponse }> = ({ data }) => {
  const { estatisticas, top_fornecedores, top_cfops } = data;

  const fonteDadosLabel = React.useMemo(() => {
    if (!data.fonte_dados) return null;
    if (data.fonte_dados === 'E110') {
      return 'Bloco E110 (Apuração Oficial)';
    }
    if (data.fonte_dados === 'C170') {
      return 'Bloco C170 (Notas de Entrada)';
    }
    return data.fonte_dados;
  }, [data.fonte_dados]);

  return (
    <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {fonteDadosLabel && (
            <Chip label={`Fonte dos valores: ${fonteDadosLabel}`} color="default" variant="outlined" />
          )}
          {Array.isArray(data.sped_fiscal_ids) && data.sped_fiscal_ids.length > 0 && (
            <Chip
              label={`SPED(s) considerados: ${data.sped_fiscal_ids.join(', ')}`}
              color="info"
              variant="outlined"
            />
          )}
        </Box>

      {/* Estatísticas Gerais */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Chip label={`Crédito Total: ${formatCurrency(estatisticas.total_credito_icms)}`} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Chip label={`Ticket Médio: ${formatCurrency(estatisticas.ticket_medio)}`} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Chip label={`Notas: ${estatisticas.total_documentos}`} color="info" />
        </Grid>
      </Grid>

      {/* Top Fornecedores */}
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        Top Fornecedores
      </Typography>
      <Table size="small" sx={{ mb: 4 }}>
        <TableHead>
          <TableRow>
            <TableCell>Fornecedor</TableCell>
            <TableCell>CNPJ/CPF</TableCell>
            <TableCell align="right">Crédito ICMS</TableCell>
            <TableCell align="right">Docs</TableCell>
            <TableCell align="right">% Part.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {top_fornecedores.map((f) => (
            <TableRow key={f.nome}>
              <TableCell>{f.nome}</TableCell>
              <TableCell>{f.cnpj_cpf}</TableCell>
              <TableCell align="right">{formatCurrency(f.total_credito_icms)}</TableCell>
              <TableCell align="right">{f.documentos}</TableCell>
              <TableCell align="right">{(f.participacao_percentual || 0).toFixed(2)}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Top CFOPs */}
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        Top CFOPs
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>CFOP</TableCell>
            <TableCell align="right">Crédito ICMS</TableCell>
            <TableCell align="right">Docs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {top_cfops.map((c) => (
            <TableRow key={c.cfop}>
              <TableCell>{c.cfop}</TableCell>
              <TableCell align="right">{formatCurrency(c.total_credito_icms)}</TableCell>
              <TableCell align="right">{c.documentos}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default IcmsCreditosPeriodoDrillDownContent; 